import { IUnitServiceHistoryDto, ServiceDocumentType, UnitDetailsDto } from 'api/api';
import { Button } from 'primereact/button';
import React, { useCallback, useEffect, useState } from 'react';
import { localized, localizedInterpolation } from 'state/i18n';
import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import { getMachineFromIdDataMapOrDefault } from 'utilities/machine-util';
import BiButton from 'view/shared/components/buttons/bi-button/bi-button';
import { getDateToString } from 'view/shared/components/date-to-string';
import { UploadServiceHistoryDialog } from 'view/shared/components/upload-service-history/upload-service-history-dialog';
import { getIcon } from 'view/shared/utils/icon-helper';
import './latest-unit-service-history-document.scss';
import { VerticalBaler } from 'utilities/constants';
import { getLatestServiceDocumentTypes, openServiceDocument } from 'state/ducks/unit-service-history/operations';

interface AppProps {
	unitId: number;
	serialNumber: string;
	serviceDocuments: IUnitServiceHistoryDto[];
	canUpload: boolean;
}

export const LatestUnitServiceHistoryDocuments: React.FC<AppProps> = ({ unitId, serialNumber, serviceDocuments, canUpload }) => {
	const dispatch = useAppDispatch();
	const [showUploadDialog, setShowUploadDialog] = useState(false);
	const { machineDetails } = useAppSelector(state => state.machineDetailsReducer);
	const currentMachine = getMachineFromIdDataMapOrDefault(machineDetails, serialNumber, UnitDetailsDto);
	const isVerticalBaler = currentMachine.data.unitTypeName === VerticalBaler;
	
	useEffect(() => {
		if (unitId) {
			getLatestServiceDocumentTypes(unitId)(dispatch);
		}
	}, [unitId, dispatch]);

	const handleShowUploadDialog = useCallback(() => {
		setShowUploadDialog(true);
	}, []);

	const hideDialog = useCallback(() => {
		setShowUploadDialog(false);
	}, []);

	const serviceTypeLinks: Record<ServiceDocumentType, string> = {
		[ServiceDocumentType.Service]: 'https://form.jotform.com/232143406020337',
		[ServiceDocumentType.SafetyCheck]: 'https://form.jotform.com/232221397612350',
		[ServiceDocumentType.Sat]: 'https://form.jotform.com/251122992141349',
	};

	const onOpenLink = (serviceType: ServiceDocumentType) => {
		const baseUrl = serviceTypeLinks[serviceType];
		const url = `${baseUrl}`;
		window.open(url, '_blank');
	};

	const getServiceDocumentButtons = (): JSX.Element[] => {
		const latestDocsByType = new Map<ServiceDocumentType | undefined, IUnitServiceHistoryDto>();

		// Find the latest document for each service type
		for (const doc of serviceDocuments) {
			const existing = latestDocsByType.get(doc.serviceDocumentType);
			if (!existing || new Date(doc.dateOfService) > new Date(existing.dateOfService)) {
				latestDocsByType.set(doc.serviceDocumentType, doc);
			}
		}

		const latestDocsArray = Array.from(latestDocsByType.values());

		// Sort the documents by service type so the same type of service document are always shown first no matter the date
		latestDocsArray.sort((a, b) => {
			const typeA = a.serviceDocumentType ?? Number.MAX_SAFE_INTEGER;
			const typeB = b.serviceDocumentType ?? Number.MAX_SAFE_INTEGER;

			return typeA - typeB;
		});

		// Create a button for each document
		return latestDocsArray.map(doc => {
			const typeName = doc.serviceDocumentType !== undefined ? ServiceDocumentType[doc.serviceDocumentType] : '';
			const localizedType = typeName ? localized(typeName as any) : localized('Unknown');
			return (
				<BiButton
					key={doc.id}
					onClick={() => doc.fileName && openServiceDocument(unitId, doc.id)}
					colorTheme={'org-white'}
					containerClassName={'document-btn-container'}
				>
					<div className="document-btn-content">
						<span>{localizedType}</span>
						<div className="date-icon-group">
							<span>{getDateToString(doc.dateOfService)}</span>
							<div className="margin-left-20px">{getIcon('pdf', 30)}</div>
						</div>
					</div>
				</BiButton>
			);
		});
	};

	return (
		<>
			<div className="latest-unit-service-history-document-header margin-top-40">
				<h3 className="text-bold">{localized('ServiceHistory')}</h3>
				{canUpload && (
					<Button
						className="p-button-background-color-metal-grey"
						label={localized('UploadDocument')}
						onClick={handleShowUploadDialog}
					/>
				)}
			</div>
			{isVerticalBaler && canUpload && (
				<div className="service-history-actions">
					<Button
						className="p-button-background-color-metal-grey"
						label={localizedInterpolation('CreateNewService', {
							serviceType: localized('Service').toLowerCase(),
						})}
						onClick={() => onOpenLink(ServiceDocumentType.Service)}
					/>
					<Button
						className="p-button-background-color-metal-grey"
						label={localizedInterpolation('CreateNewService', {
							serviceType: localized('SafetyCheck').toLowerCase(),
						})}
						onClick={() => onOpenLink(ServiceDocumentType.SafetyCheck)}
					/>
					<Button
						className="p-button-background-color-metal-grey"
						label={localizedInterpolation('CreateNewService', { serviceType: localized('Sat') })}
						onClick={() => onOpenLink(ServiceDocumentType.Sat)}
					/>
				</div>
			)}
			{serviceDocuments.length > 0 ? (
				<>{getServiceDocumentButtons()}</>
			) : (
				<div className="text-center no-documents-found">{localized('NoServiceHistoryDocumentsFound')}</div>
			)}

			{canUpload && (
				<UploadServiceHistoryDialog
					unitId={unitId}
					visible={showUploadDialog}
					onSave={hideDialog}
					onCancel={hideDialog}
				/>
			)}
		</>
	);
};
